import { Loader2, Check, X, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AssetStatus } from '@/types/service';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface AssetStatusIconProps {
  /** Current asset status */
  status: AssetStatus;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Icon showing the validation status of an external asset.
 *
 * | Status  | Icon | Color |
 * |---------|------|-------|
 * | empty   | -    | Gray  |
 * | loading | spin | Blue  |
 * | success | check| Green |
 * | error   | x    | Red   |
 */
export function AssetStatusIcon({ status, className }: AssetStatusIconProps) {
  const iconClass = cn('h-4 w-4', className);

  switch (status.status) {
    case 'empty':
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Minus className={cn(iconClass, 'text-muted-foreground')} data-testid="asset-status-empty" />
          </TooltipTrigger>
          <TooltipContent>
            <p>Kein Asset konfiguriert</p>
          </TooltipContent>
        </Tooltip>
      );

    case 'loading':
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Loader2 className={cn(iconClass, 'text-blue-500 animate-spin')} data-testid="asset-status-loading" />
          </TooltipTrigger>
          <TooltipContent>
            <p>Asset wird geladen...</p>
          </TooltipContent>
        </Tooltip>
      );

    case 'success':
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Check className={cn(iconClass, 'text-green-500')} data-testid="asset-status-success" />
          </TooltipTrigger>
          <TooltipContent>
            <p>Asset geladen ({Math.round(status.size / 1024)} KB)</p>
          </TooltipContent>
        </Tooltip>
      );

    case 'error':
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <X className={cn(iconClass, 'text-destructive')} data-testid="asset-status-error" />
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-destructive">{status.message}</p>
          </TooltipContent>
        </Tooltip>
      );
  }
}
